// Graphics for "Building Precise Passers: A Deep Dive".
//
// Rebuilt 2026-10-04. The first set was five static panels in a borrowed
// palette: blue offense, red defense, green good, orange ball, amber warning.
// Two things were wrong with that, and neither was a detail.
//
// The palette was not ours. Five saturated hues off a default chart ramp, with
// green and amber implying a traffic-light verdict that is not the point, and
// no gold anywhere. A coach's mental model is a whiteboard, so this set is two
// hues plus neutrals: light strokes for offense and figures, red for the
// defender, gold for the ball and its target. Gold is the brand colour and it
// now marks the one thing every panel is about, which is where the ball goes.
//
// And they were static, while half this masterclass is about TIME: windows
// that open and shut, information arriving late, leading a target, the extra
// beat a bad pass costs. A still frame can assert those. It cannot show them.
// So four of the five are now loops and the fifth is something you operate.
//
// Each loop runs only while it is on screen, holds a representative frame
// under prefers-reduced-motion, and makes exactly one claim. No panel is
// load-bearing for the argument: the words are, and a graphic that fails to
// load or animate costs the reader nothing.

import React, { useEffect, useRef, useState } from "react";

/* ── Palette ──────────────────────────────────────────────────────────────── */

/**
 * Two hues and neutrals, resolved per theme.
 *
 * The light values are not the dark ones: #E2DD9F is a pale gold built for
 * black and is close to invisible on white, and the same is true of a bright
 * red. Both get taken down until they hold their own against the page.
 */
export const pal = (dark) => ({
  ink:    dark ? "#F2F2F0" : "#191919",
  inkDim: dark ? "rgba(242,242,240,0.34)" : "rgba(25,25,25,0.28)",
  def:    dark ? "#E0655E" : "#B83A30",
  ball:   dark ? "#E2DD9F" : "#8A7F2A",
  zone:   dark ? "rgba(226,221,159,0.17)" : "rgba(138,127,42,0.15)",
  rule:   dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.11)",
  label:  dark ? "rgba(242,242,240,0.60)" : "rgba(25,25,25,0.55)",
  strong: dark ? "rgba(242,242,240,0.88)" : "rgba(25,25,25,0.82)",
});

/* ── Motion ───────────────────────────────────────────────────────────────── */

const prefersReduced = () =>
  typeof window !== "undefined"
  && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * A 0..1 loop phase, driven by rAF, paused while off screen.
 *
 * Paused rather than merely hidden, because five looping panels in one
 * article would otherwise all be animating behind each other for the whole
 * read. Under reduced motion it returns one fixed phase and never ticks, so
 * the panel still shows a frame that makes the point.
 */
function useLoop(ms, stillPhase = 0.62) {
  const hostRef = useRef(null);
  const [t, setT] = useState(stillPhase);

  useEffect(() => {
    if (prefersReduced()) { setT(stillPhase); return; }
    const el = hostRef.current;
    let raf = 0, started = null, onScreen = true, io = null;

    if (el && typeof IntersectionObserver !== "undefined") {
      onScreen = false;
      io = new IntersectionObserver(
        (es) => { onScreen = es.some((e) => e.isIntersecting); },
        { rootMargin: "80px 0px" },
      );
      io.observe(el);
    }
    const tick = (now) => {
      if (started === null) started = now;
      if (onScreen) setT(((now - started) % ms) / ms);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); if (io) io.disconnect(); };
  }, [ms, stillPhase]);

  return [t, hostRef];
}

/* ── Primitives ───────────────────────────────────────────────────────────── */

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
/** Progress through a window of the loop, 0 before it, 1 after it. */
const seg = (t, from, to) => clamp01((t - from) / (to - from));
const lerp = (a, b, k) => a + (b - a) * k;
const ease = (k) => (k < 0.5 ? 2 * k * k : 1 - 2 * (1 - k) * (1 - k));
const pt = (x, y) => ({ x, y });
const mix = (p, q, k) => pt(lerp(p.x, q.x, k), lerp(p.y, q.y, k));

const Frame = ({ children, label, vb = "0 0 320 200", hostRef }) => (
  <svg ref={hostRef} viewBox={vb} style={{ width: "100%", height: "auto", display: "block" }}
    role="img" aria-label={label}>{children}</svg>
);

const Tag = ({ x, y, text, color, anchor = "middle", size = 9.5 }) => (
  <text x={x} y={y} textAnchor={anchor} fontSize={size} fontWeight="700"
    letterSpacing="0.1em" fill={color}>{text}</text>
);

/** A player from the side, as a few strokes. Arms are given absolute so the
 *  caller animates the pose; everything else is fixed to the ground line. */
function Figure({ x, ground, s = 1, armA, armB, c, lw = 2.3, dim }) {
  const hip = ground - 23 * s, sh = ground - 37 * s, head = ground - 45 * s;
  return (
    <g stroke={c} strokeWidth={lw} strokeLinecap="round" fill="none" opacity={dim ? 0.3 : 1}>
      <circle cx={x} cy={head} r={5.2 * s} />
      <line x1={x} y1={head + 5.2 * s} x2={x} y2={hip} />
      <line x1={x} y1={hip} x2={x - 7.5 * s} y2={ground} />
      <line x1={x} y1={hip} x2={x + 7 * s} y2={ground} />
      <line x1={x} y1={sh} x2={armA.x} y2={armA.y} />
      <line x1={x} y1={sh} x2={armB.x} y2={armB.y} />
    </g>
  );
}

/** Arm poses, relative to a figure's shoulder. */
const pose = (x, ground, s = 1) => {
  const sh = ground - 37 * s;
  return {
    ready: [pt(x - 9 * s, sh + 15 * s), pt(x + 9 * s, sh + 15 * s)],
    chest: [pt(x - 7 * s, sh + 5 * s), pt(x + 7 * s, sh + 5 * s)],
    shoot: [pt(x - 4 * s, sh - 17 * s), pt(x + 5 * s, sh - 18 * s)],
    wide:  [pt(x - 6 * s, sh + 12 * s), pt(x + 23 * s, sh - 1 * s)],
    high:  [pt(x - 3 * s, sh - 19 * s), pt(x + 6 * s, sh - 21 * s)],
    low:   [pt(x - 7 * s, sh + 16 * s), pt(x + 11 * s, ground - 9 * s)],
  };
};

const Ball = ({ x, y, c, r = 4.6 }) => (
  <g><circle cx={x} cy={y} r={r} fill={c} />
    <circle cx={x} cy={y} r={r} fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="0.8" /></g>
);

const Ex = ({ x, y, s = 7, c, lw = 2.6, dim }) => (
  <g stroke={c} strokeWidth={lw} strokeLinecap="round" opacity={dim ? 0.28 : 1}>
    <line x1={x - s} y1={y - s} x2={x + s} y2={y + s} />
    <line x1={x + s} y1={y - s} x2={x - s} y2={y + s} />
  </g>
);

/** A dot that fills as a beat is spent. The cost, counted. */
const Beats = ({ x, y, n, filled, c, dimc }) => (
  <g>{Array.from({ length: n }, (_, i) => (
    <rect key={i} x={x + i * 13} y={y} width={9} height={4} rx={2}
      fill={i < filled ? c : dimc} />
  ))}</g>
);

/* ── 1. The strike zone ───────────────────────────────────────────────────── */
//
// The claim is NOT where the ball lands. It is what landing there costs the
// receiver before they can shoot. The old panel diagrammed five arrival
// points with a legend, which named the locations and never showed the cost.
// Two receivers on one clock do: the left one catches and shoots, the right
// one catches, brings it in, resets, and is still going when the left is
// finished. The beat row counts it.

function StrikeZone({ dark }) {
  const P = pal(dark);
  const [t, ref] = useLoop(5600, 0.55);
  const GROUND = 150;

  const run = (x, inZone) => {
    const po = pose(x, GROUND);
    const sh = GROUND - 37;
    const incoming = seg(t, 0.04, 0.2);
    const arrive = inZone ? pt(x + 7, sh + 5) : pt(x + 26, sh - 1);
    const ball = mix(pt(x - 96, sh - 6), arrive, ease(incoming));

    let arms = po.ready, beats = 0, held = incoming >= 1;
    if (!held) {
      arms = po.ready;
    } else if (inZone) {
      // Catch, then straight up into the shot. Two beats.
      const k = seg(t, 0.2, 0.42);
      arms = [mix(po.chest[0], po.shoot[0], ease(k)), mix(po.chest[1], po.shoot[1], ease(k))];
      beats = k >= 1 ? 2 : k > 0 ? 1 : 0;
    } else {
      // Caught outside the frame: bring it in, reset the base, then shoot.
      const a = seg(t, 0.2, 0.36), b = seg(t, 0.36, 0.54), c2 = seg(t, 0.54, 0.78);
      if (c2 > 0) arms = [mix(po.chest[0], po.shoot[0], ease(c2)), mix(po.chest[1], po.shoot[1], ease(c2))];
      else if (b > 0) arms = [mix(po.wide[0], po.chest[0], ease(b)), mix(po.wide[1], po.chest[1], ease(b))];
      else arms = po.wide;
      beats = (a > 0 ? 1 : 0) + (b > 0 ? 1 : 0) + (c2 > 0 ? 1 : 0) + (c2 >= 1 ? 1 : 0);
    }
    const ballWithHands = held ? (beats >= (inZone ? 2 : 4) ? null : arms[1]) : ball;
    return { po, arms, beats, ballWithHands, sh };
  };

  const L = run(84, true), R = run(236, false);
  const done = (r, n) => r.beats >= n;

  return (
    <Frame hostRef={ref} vb="0 0 320 200"
      label="Two receivers on one clock: a pass into the strike zone flows into the shot in two beats, a pass outside it takes four">
      <line x1={160} y1={26} x2={160} y2={176} stroke={P.rule} strokeWidth="1" />
      <line x1={14} y1={GROUND} x2={146} y2={GROUND} stroke={P.rule} strokeWidth="1.2" />
      <line x1={174} y1={GROUND} x2={306} y2={GROUND} stroke={P.rule} strokeWidth="1.2" />

      {/* Left: into the zone. */}
      <Tag x={84} y={22} text="IN THE ZONE" color={P.ball} />
      <rect x={84 - 17} y={L.sh - 4} width={34} height={22} rx={7}
        fill={P.zone} stroke={P.ball} strokeWidth="1.2" strokeDasharray="4 3" />
      <Figure x={84} ground={GROUND} armA={L.arms[0]} armB={L.arms[1]} c={P.ink} />
      {L.ballWithHands && <Ball x={L.ballWithHands.x} y={L.ballWithHands.y} c={P.ball} />}
      {done(L, 2) && <Tag x={84} y={GROUND - 66} text="SHOT" color={P.ball} size={9} />}
      <Beats x={84 - 25} y={GROUND + 14} n={4} filled={L.beats} c={P.ball} dimc={P.rule} />
      <Tag x={84} y={GROUND + 33} text="2 BEATS" color={P.strong} size={9} />

      {/* Right: outside it. Same catch, more to do. */}
      <Tag x={236} y={22} text="OUTSIDE IT" color={P.def} />
      <rect x={236 - 17} y={R.sh - 4} width={34} height={22} rx={7}
        fill="none" stroke={P.rule} strokeWidth="1.2" strokeDasharray="4 3" />
      <Figure x={236} ground={GROUND} armA={R.arms[0]} armB={R.arms[1]} c={P.ink} />
      {R.ballWithHands && <Ball x={R.ballWithHands.x} y={R.ballWithHands.y} c={P.ball} />}
      {done(R, 4) && <Tag x={236} y={GROUND - 66} text="SHOT" color={P.ball} size={9} />}
      <Beats x={236 - 25} y={GROUND + 14} n={4} filled={R.beats} c={P.def} dimc={P.rule} />
      <Tag x={236} y={GROUND + 33} text="4 BEATS" color={P.strong} size={9} />
    </Frame>
  );
}

/* ── 3. Release windows ───────────────────────────────────────────────────── */
//
// The best of the old five, kept and made to move. Three paths drawn at once
// says "there are three". A defender whose arm moves while the live path
// changes underneath says "take one away and another opens", which is the
// sentence the chapter actually makes.

function ReleaseWindows({ dark }) {
  const P = pal(dark);
  const [t, ref] = useLoop(6600, 0.2);
  const FLOOR = 170;
  const phase = Math.floor(t * 3) % 3;         // 0 arm high, 1 middle, 2 low
  const local = (t * 3) % 1;

  // The contest moves; the route that survives it changes.
  const armY = [68, 100, 132][phase];
  const routes = [
    { id: 0, d: "M56 118 L 150 164 L 254 118", label: "OFF THE FLOOR", dash: "7 4" },
    { id: 1, d: "M54 86 Q 156 30 256 94", label: "OVER THE TOP", dash: null },
    { id: 2, d: "M58 116 L 252 116", label: "PAST THE HIP", dash: null },
  ];
  const liveId = phase;      // high arm -> floor, middle -> over, low -> hip
  const draw = ease(seg(local, 0.1, 0.72));

  return (
    <Frame hostRef={ref} vb="0 0 320 200"
      label="One defender contesting high, then middle, then low, and the release that stays available each time">
      <line x1={16} y1={FLOOR} x2={304} y2={FLOOR} stroke={P.rule} strokeWidth="1.2" />

      <circle cx={44} cy={104} r={12} fill={P.ink} />
      <text x={44} y={108} textAnchor="middle" fontSize="10.5" fontWeight="800"
        fill={dark ? "#111" : "#FFF"}>P</text>
      <circle cx={268} cy={106} r={12} fill="none" stroke={P.ink} strokeWidth="2.3" />
      <text x={268} y={110} textAnchor="middle" fontSize="10.5" fontWeight="800" fill={P.ink}>R</text>
      <ellipse cx={268} cy={106} rx={21} ry={17} fill={P.zone} stroke={P.ball}
        strokeWidth="1.2" strokeDasharray="3 3" />
      <Tag x={268} y={148} text="SAME TARGET" color={P.ball} size={9} />

      {/* The defender, and the hand that is taking a window away. */}
      <Ex x={92} y={104} s={9} c={P.def} />
      <line x1={92} y1={104} x2={92} y2={armY} stroke={P.def} strokeWidth="2.4"
        strokeLinecap="round" />
      <circle cx={92} cy={armY} r={4} fill={P.def} />

      {/* Only the route that survives this contest. Drawing all three at
          once is what made the old panel a diagram of options rather than a
          demonstration that one of them is gone. */}
      <path d={routes[liveId].d} fill="none" stroke={P.ball} strokeWidth="2.6"
        strokeDasharray={routes[liveId].dash || undefined} opacity={0.35 + draw * 0.65} />
      <BallOnPath d={routes[liveId].d} k={draw} c={P.ball} />
      <Tag x={160} y={192} text={routes[liveId].label} color={P.ball} />
      <Tag x={160} y={22} text="THE CONTEST MOVES" color={P.label} size={9} />
    </Frame>
  );
}

/* ── 4. A moving receiver ─────────────────────────────────────────────────── */
//
// Labelling three positions WAS, IS and WILL BE tells you a target moves. It
// does not show why throwing at the one you can see fails. So the same pass
// is thrown twice: at them, and ahead of them.

function MovingTarget({ dark }) {
  const P = pal(dark);
  const [t, ref] = useLoop(7000, 0.78);
  const half = t < 0.5 ? 0 : 1;             // 0: thrown at them. 1: thrown ahead.
  const local = (t % 0.5) / 0.5;

  const runPath = (k) => pt(lerp(112, 268, k), lerp(150, 66, k));
  const rK = ease(clamp01(local / 0.84));
  const R = runPath(rK);

  // The lead pass has to ARRIVE as the receiver does, or the ball sits on the
  // spot waiting for them and the panel teaches the opposite of a lead.
  // Release at 0.26, arrival at 0.84, which is when the run finishes.
  const throwK = ease(seg(local, 0.26, 0.84));
  const aimedAt = runPath(ease(clamp01(0.26 / 0.84)));   // where they were at release
  const aimedAhead = runPath(1);
  const target = half ? aimedAhead : aimedAt;
  const ball = mix(pt(59, 111), target, throwK);  // beside P, not over the letter
  const landed = throwK >= 1;
  const caught = half && landed;
  const missed = !half && landed;

  return (
    <Frame hostRef={ref} vb="0 0 320 200"
      label="The same pass thrown at a moving receiver and then thrown ahead of them">
      {/* The run, and the ghosts of where they have been. */}
      <path d="M112 150 Q 190 128 268 66" stroke={P.rule} strokeWidth="1.4"
        fill="none" strokeDasharray="4 5" />
      {[0.25, 0.5, 0.75].map((g) => {
        const q = runPath(g);
        return <circle key={g} cx={q.x} cy={q.y} r={10} fill="none"
          stroke={P.ink} strokeWidth="1.3" opacity={0.18} />;
      })}

      <circle cx={44} cy={118} r={12} fill={P.ink} />
      <text x={44} y={122} textAnchor="middle" fontSize="10.5" fontWeight="800"
        fill={dark ? "#111" : "#FFF"}>P</text>

      <circle cx={R.x} cy={R.y} r={11} fill="none" stroke={P.ink} strokeWidth="2.3" />
      <text x={R.x} y={R.y + 4} textAnchor="middle" fontSize="10" fontWeight="800" fill={P.ink}>R</text>

      {/* Where it was aimed. */}
      <circle cx={target.x} cy={target.y} r={15} fill="none"
        stroke={half ? P.ball : P.def} strokeWidth="1.4" strokeDasharray="3 3" opacity={0.8} />
      <Ball x={ball.x} y={ball.y} c={half ? P.ball : P.def} />

      {missed && (
        <>
          <Ex x={target.x} y={target.y} s={8} c={P.def} lw={2.8} />
          <Tag x={160} y={190} text="THROWN AT THEM — ARRIVES BEHIND" color={P.def} size={9} />
        </>
      )}
      {caught && <Tag x={160} y={190} text="THROWN AHEAD — ARRIVES IN STRIDE" color={P.ball} size={9} />}
      {!landed && (
        <Tag x={160} y={190} text={half ? "AIMING AHEAD OF THE RUN" : "AIMING AT THE RUN"}
          color={P.label} size={9} />
      )}
      <Tag x={160} y={22} text="THE TARGET IS A TIME, NOT A PLACE" color={P.label} size={9} />
    </Frame>
  );
}

/* ── 5. The toolbox ───────────────────────────────────────────────────────── */
//
// Twenty identical dots in a grid told you there are twenty solutions without
// showing one of them. So this is the one panel that is operated rather than
// watched: pick a release and a path and it draws that actual pass. Twenty
// becomes something a coach does instead of a number they read, and the
// counter is the only scorekeeping, because trying them all is the point.

/**
 * The toolbox figure, rigged rather than posed.
 *
 * The other four graphics use the plain `Figure`: head, spine, two straight
 * arms, two straight legs. That is enough when the figure is only saying
 * "a player is here". It is not enough here, because this graphic's whole
 * claim is that a release point is a CHOICE, and five releases drawn as
 * five straight lines out of one shoulder all look like the same throw at a
 * different angle.
 *
 * So each release carries a skeleton: an elbow, so the arm reads as an arm
 * and the hook's elbow can be high and across while the push's is locked
 * out; a knee and a stance, because you cannot throw a wide release off a
 * square base; and a lean, because the body goes with the ball.
 *
 * All offsets are from the shoulder (arms, lean) or the hip (legs), x right
 * and y down, in unscaled units. `hand` on the throwing arm is where the
 * ball actually leaves, so the pass starts from the drawn hand instead of
 * from a separately tuned coordinate that used to drift away from it.
 */
const RELEASES = [
  {
    id: "chest", label: "Chest",
    lean: 0,
    off:   { elbow: [-11, 5],  hand: [-4, 8] },
    throw: { elbow: [11, 5],   hand: [4, 8] },
    legs:  { back: { knee: [-6, 12], foot: [-9, 23] }, front: { knee: [6, 12], foot: [9, 23] } },
  },
  {
    id: "overhead", label: "Overhead",
    lean: 0,
    off:   { elbow: [-10, -5], hand: [-5, -18] },
    throw: { elbow: [10, -5],  hand: [5, -18] },
    legs:  { back: { knee: [-7, 12], foot: [-10, 23] }, front: { knee: [7, 11], foot: [11, 23] } },
  },
  {
    id: "push", label: "Push",
    // One hand, arm locked out to the side, off a staggered base.
    lean: 3,
    off:   { elbow: [-8, 8],   hand: [-7, 15] },
    throw: { elbow: [13, 1],   hand: [26, -3] },
    legs:  { back: { knee: [-8, 13], foot: [-13, 23] }, front: { knee: [11, 11], foot: [17, 23] } },
  },
  {
    id: "hook", label: "Hook",
    // The arm comes over the top from the far side, which is the only
    // reason the elbow has to be drawn: without it this is just "overhead".
    lean: -4,
    off:   { elbow: [-10, 7],  hand: [-14, 15] },
    throw: { elbow: [13, -11], hand: [1, -25] },
    legs:  { back: { knee: [-10, 12], foot: [-15, 23] }, front: { knee: [4, 13], foot: [5, 23] } },
  },
  {
    id: "wide", label: "Wide",
    // Low and away from the frame, which costs a deep knee on that side.
    lean: 5,
    off:   { elbow: [-8, 8],   hand: [-9, 15] },
    throw: { elbow: [14, 8],   hand: [27, 13] },
    legs:  { back: { knee: [-9, 11], foot: [-13, 23] }, front: { knee: [13, 10], foot: [20, 23] } },
  },
];

/** Shoulder point of the rigged figure, which every arm offset hangs off. */
const rigShoulder = (x, ground, s, R) => pt(x + (R.lean || 0) * s, ground - 37 * s);
/** Where the ball leaves: the throwing hand itself. */
const rigRelease = (x, ground, s, R) => {
  const sh = rigShoulder(x, ground, s, R);
  return pt(sh.x + R.throw.hand[0] * s, sh.y + R.throw.hand[1] * s);
};

function RiggedFigure({ x, ground, s = 1, R, c, lw = 2.3 }) {
  const sh = rigShoulder(x, ground, s, R);
  const hip = pt(x, ground - 23 * s);
  const head = pt(sh.x + (R.lean || 0) * 0.35 * s, sh.y - 10.5 * s);
  const limb = (base, j) => `${base.x} ${base.y} ${base.x + j.elbow[0] * s} ${base.y + j.elbow[1] * s} ${base.x + j.hand[0] * s} ${base.y + j.hand[1] * s}`;
  const leg = (j) => `${hip.x} ${hip.y} ${hip.x + j.knee[0] * s} ${hip.y + j.knee[1] * s} ${hip.x + j.foot[0] * s} ${hip.y + j.foot[1] * s}`;
  const hand = (j) => pt(sh.x + j.hand[0] * s, sh.y + j.hand[1] * s);
  return (
    <g stroke={c} strokeWidth={lw} strokeLinecap="round" strokeLinejoin="round" fill="none">
      <circle cx={head.x} cy={head.y} r={5.4 * s} />
      <line x1={head.x} y1={head.y + 5.4 * s} x2={hip.x} y2={hip.y} />
      {/* A shoulder bar, so the torso has a front and the lean is legible. */}
      <line x1={sh.x - 6.5 * s} y1={sh.y} x2={sh.x + 6.5 * s} y2={sh.y} strokeWidth={lw * 0.8} />
      <polyline points={leg(R.legs.back)} opacity={0.72} />
      <polyline points={leg(R.legs.front)} />
      <polyline points={limb(sh, R.off)} opacity={0.72} />
      <polyline points={limb(sh, R.throw)} />
      {[R.off, R.throw].map((j, i) => {
        const h = hand(j);
        return <circle key={i} cx={h.x} cy={h.y} r={1.9 * s} fill={c} stroke="none" />;
      })}
    </g>
  );
}
const PATHS = [
  { id: "direct", label: "Direct" },
  { id: "lob",    label: "Lob" },
  { id: "bounce", label: "Bounce" },
  { id: "skip",   label: "Skip" },
];

function Toolbox({ dark, C }) {
  const P = pal(dark);
  const [rel, setRel] = useState("chest");
  const [path, setPath] = useState("direct");
  const [tried, setTried] = useState(() => new Set(["chest|direct"]));
  const [t, ref] = useLoop(2600, 0.85);

  const choose = (r, p) => {
    setRel(r); setPath(p);
    setTried((s) => new Set(s).add(`${r}|${p}`));
  };

  const R = RELEASES.find((x) => x.id === rel) || RELEASES[0];
  const GROUND = 120, PX = 66, TX = 264, TY = 72, FIG = 1.28;
  // The pass starts at the hand that throws it, derived from the same rig
  // that draws the arm, so the two can never disagree.
  const from = rigRelease(PX, GROUND, FIG, R);
  const d = {
    direct: `M${from.x} ${from.y} L ${TX} ${TY}`,
    lob:    `M${from.x} ${from.y} Q ${(from.x + TX) / 2} ${Math.min(from.y, TY) - 46} ${TX} ${TY}`,
    bounce: `M${from.x} ${from.y} L ${(from.x + TX) / 2 - 6} ${GROUND - 4} L ${TX} ${TY}`,
    skip:   `M${from.x} ${from.y} Q ${(from.x + TX) / 2} ${from.y + 26} ${TX} ${TY}`,
  }[path];

  const chip = (on) => ({
    padding: "5px 10px", borderRadius: 7, fontSize: 11.5, fontWeight: 700,
    cursor: "pointer", fontFamily: "inherit",
    border: `1px solid ${on ? P.ball : P.rule}`,
    background: on ? (dark ? "rgba(226,221,159,0.14)" : "rgba(138,127,42,0.10)") : "transparent",
    color: on ? P.ball : P.label,
  });

  return (
    <div>
      <svg ref={ref} viewBox="0 0 320 152" style={{ width: "100%", height: "auto", display: "block" }}
        role="img" aria-label={`A ${R.label.toLowerCase()} release on a ${path} path`}>
        <line x1={16} y1={GROUND} x2={304} y2={GROUND} stroke={P.rule} strokeWidth="1.2" />
        <RiggedFigure x={PX} ground={GROUND} s={FIG} R={R} c={P.ink} lw={2.5} />
        <circle cx={TX} cy={TY} r={12} fill="none" stroke={P.ink} strokeWidth="2.3" />
        <text x={TX} y={TY + 4} textAnchor="middle" fontSize="10.5" fontWeight="800" fill={P.ink}>R</text>
        <ellipse cx={TX} cy={TY} rx={21} ry={17} fill={P.zone} stroke={P.ball}
          strokeWidth="1.2" strokeDasharray="3 3" />
        <path d={d} fill="none" stroke={P.ball} strokeWidth="2.5"
          strokeDasharray={path === "bounce" ? "7 4" : undefined} />
        <path d={d} fill="none" stroke="none" />
        {/* The ball runs the chosen path so the choice is a pass, not a line. */}
        <BallOnPath d={d} k={ease(t)} c={P.ball} />
      </svg>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
        {RELEASES.map((r) => (
          <button key={r.id} type="button" onClick={() => choose(r.id, path)}
            style={chip(r.id === rel)}>{r.label}</button>
        ))}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 7 }}>
        {PATHS.map((p) => (
          <button key={p.id} type="button" onClick={() => choose(rel, p.id)}
            style={chip(p.id === path)}>{p.label}</button>
        ))}
      </div>
      <div style={{ marginTop: 11, fontSize: 12, color: C?.textDim || P.label }}>
        {tried.size} of {RELEASES.length * PATHS.length} tried.
      </div>
    </div>
  );
}

/** Puts the ball on an arbitrary path without hand-solving the curve. */
function BallOnPath({ d, k, c }) {
  const ref = useRef(null);
  const [p, setP] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !el.getTotalLength) return;
    try {
      const len = el.getTotalLength();
      const q = el.getPointAtLength(len * clamp01(k));
      setP({ x: q.x, y: q.y });
    } catch { setP(null); }
  }, [d, k]);
  return (
    <>
      <path ref={ref} d={d} fill="none" stroke="none" />
      {p && <Ball x={p.x} y={p.y} c={c} />}
    </>
  );
}

/* ── Registry ─────────────────────────────────────────────────────────────── */

const GRAPHICS = {
  "strike-zone": StrikeZone,
  "release-windows": ReleaseWindows,
  "moving-target": MovingTarget,
  toolbox: Toolbox,
};

// Graphic 2, "training against the game", was removed on 2026-10-05:
// Coleman's call that the page did not need it. The numbering below keeps
// its gap rather than renumbering, so the section comments still line up
// with git history. It is one revert away if he wants it back.
export const ACCURACY_GRAPHIC_NAMES = Object.keys(GRAPHICS);

export function AccuracyGraphic({ name, caption, C, article, dark = true }) {
  const G = GRAPHICS[name];
  // An unknown name renders nothing rather than an error box: a graphic is
  // never load-bearing for the argument, the words are.
  const [zoomed, setZoomed] = useState(false);

  // Escape closes, and the page behind stops scrolling while it is open.
  useEffect(() => {
    if (!zoomed) return;
    const onKey = (e) => { if (e.key === "Escape") setZoomed(false); };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [zoomed]);

  if (!G) return null;
  return (
    <figure style={{
      margin: "26px 0", padding: "16px 14px 14px",
      borderRadius: 14,
      background: C.cardGradient || "rgba(255,255,255,0.02)",
      border: `1px solid ${C.border}`,
    }}>
      {/* Tap to fill the screen. These are drawn for a 700px column and
          shown at about 330px on a phone, where the toolbox figure in
          particular loses the joints that are the whole point of it.
          A button rather than an onClick div, so it is reachable by
          keyboard and announced as an action. The toolbox has its own
          controls, so zooming it would trap the taps that drive it. */}
      {name === "toolbox" ? (
        <G C={C} dark={dark} />
      ) : (
        <button type="button" onClick={() => setZoomed(true)}
          aria-label={`Expand graphic${caption ? `: ${caption}` : ""}`}
          style={{ display: "block", width: "100%", padding: 0, border: "none",
            background: "transparent", cursor: "zoom-in", font: "inherit", textAlign: "left" }}>
          <G C={C} dark={dark} />
        </button>
      )}

      {zoomed && (
        <div role="dialog" aria-modal="true" onClick={() => setZoomed(false)}
          style={{ position: "fixed", inset: 0, zIndex: 9999, background: dark ? "#0A0A0A" : "#FAFAFA",
            display: "flex", flexDirection: "column", justifyContent: "center",
            padding: "max(16px, env(safe-area-inset-top)) 12px max(16px, env(safe-area-inset-bottom))" }}>
          <div style={{ width: "100%" }} onClick={(e) => e.stopPropagation()}>
            <G C={C} dark={dark} />
            {caption && (
              <div style={{ marginTop: 16, fontSize: 14, lineHeight: 1.55, color: C.textDim,
                fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic" }}>{caption}</div>
            )}
          </div>
          <button type="button" onClick={() => setZoomed(false)}
            style={{ position: "absolute", top: "max(14px, env(safe-area-inset-top))", right: 14,
              minWidth: 44, minHeight: 44, borderRadius: 22, cursor: "pointer",
              border: `1px solid ${C.border}`, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
              color: C.text, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
            ×
          </button>
        </div>
      )}
      {caption && (
        <figcaption style={article ? {
          // Italic serif, so a caption reads as a caption and not as another
          // paragraph that happens to be small.
          marginTop: 14, fontSize: 15, lineHeight: 1.55, color: C.textDim,
          fontFamily: "'Fraunces', Georgia, serif", fontStyle: "italic", fontWeight: 400,
        } : {
          marginTop: 10, fontSize: 12, lineHeight: 1.55, color: C.textDim,
        }}>{caption}</figcaption>
      )}
    </figure>
  );
}

export default AccuracyGraphic;
